import React from 'react'
import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import {
  BellOutlined,
  MessageOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  UploadOutlined,
  FileSyncOutlined
} from '@ant-design/icons'
import { getFirstDayDateFormat } from 'utils/helpers/dateHelper'
import ticket_icon from 'assets/ticket_icon.svg'
import {
  CHAT_REPLY_TYPE,
  CANCEL_APPOINTMENT_TYPE,
  GROUPED_REPLY_TYPE,
  ALARM_TYPE,
  ASSIGNED_TYPE,
  TASK_TYPE,
  TASK_ASSIGNED_TYPE,
  APPOINTMENT_RED,
  ALARM_ORANGE,
  MESSAGE_BLUE,
  TASK_BLACK,
  BULK_UPLOAD_CATEGORIES_TYPE,
  BULK_UPLOAD_CATEGORIES_TYPE_ERRORS,
  BENEFIT_CATEGORIES_BULK_CREATE,
  BENEFIT_GROUPS_BULK_CREATE,
  BENEFITS_BULK_CREATE,
  BENEFIT_CATEGORIES_BULK_CREATE_ERRORS,
  BENEFIT_GROUPS_BULK_CREATE_ERRORS,
  BENEFITS_BULK_CREATE_ERRORS,
  BULK_CREATE_TICKETS,
  INSURANCE_PLAN_BULK_UPLOAD,
  INSURANCE_PLAN_BULK_UPLOAD_ERRORS,
  INSURANCE_PLAN_CLONED,
  INTERACTIONS_BULK_CREATE,
  USER_TAGGED,
  TICKET_DELIVERABLES_BULK_CREATE,
  TICKET_DELIVERABLES_BULK_CREATE_ERRORS,
  TICKET_BULK_ASSIGN_TO_PERFORMER,
  TICKET_BULK_ASSIGN_TO_QUEUE,
  TICKET_BULK_ASSIGN_TO_RECIPIENT,
  TICKET_BULK_CHANGE_STATUS,
  BENEFIT_ORGS_BULK_UPLOAD
} from './constants'

dayjs.extend(isSameOrBefore)

// Helper to wrap icon with color for AntD v6 compatibility
const createColoredIcon = (Icon, color) => (
  <span style={{ color }}>
    <Icon style={{ color }} />
  </span>
)

export const NOTIFICATIONS = {
  [CHAT_REPLY_TYPE]: {
    title: 'Chat Reply',
    description: item => `Unread Message from ${item?.data?.member_name}`,
    icon: createColoredIcon(MessageOutlined, MESSAGE_BLUE)
  },
  [CANCEL_APPOINTMENT_TYPE]: {
    title: 'Appointment canceled',
    description: item =>
      `${item?.data?.member_name} requested cancellation of their appointment ${
        item?.data?.appointment_time
          ? ` on ${getFirstDayDateFormat(item?.data?.appointment_time)}`
          : ''
      }`,
    icon: createColoredIcon(CalendarOutlined, APPOINTMENT_RED)
  },
  [GROUPED_REPLY_TYPE]: {
    title: 'Chat Reply',
    description: item =>
      item?.ids?.length > 1
        ? `${item.ids.length} Unread Messages from ${item?.data?.member_name}`
        : `Unread Message from ${item?.data?.member_name}`,
    icon: createColoredIcon(MessageOutlined, MESSAGE_BLUE)
  },
  [ALARM_TYPE]: {
    title: 'Alarm',
    description: item => `Your alarm for ${item?.data?.member_name} is due.`,
    icon: createColoredIcon(BellOutlined, ALARM_ORANGE)
  },
  [ASSIGNED_TYPE]: {
    title: 'Ticket assigned',
    description: item =>
      `${item?.data?.performer_name} assigned you a ${item?.data?.ticket_type} ticket for ${item?.data?.member_name}`,
    icon: (
      <img
        src={ticket_icon}
        alt="ticket"
        style={{ width: 20, height: 20, marginRight: 12 }}
      />
    )
  },
  [TASK_TYPE]: {
    title: 'Task Due',
    description: item =>
      `Your task is due for ${item?.source_type || 'Ticket'} ${
        item?.source_id
      }.`,
    icon: createColoredIcon(CheckSquareOutlined, TASK_BLACK)
  },
  [TASK_ASSIGNED_TYPE]: {
    title: 'Task Assigned',
    description: item =>
      `${item?.data?.performer_name} assigned you a task for ${item?.data?.member_name}.`,
    icon: createColoredIcon(CheckSquareOutlined, TASK_BLACK)
  },
  [BULK_CREATE_TICKETS]: {
    title: 'Tickets bulk creation',
    description: item =>
      `Ticket bulk creation ${item.data.success ? 'completed' : 'failed'}`,
    icon: createColoredIcon(UploadOutlined, TASK_BLACK)
  },
  [BULK_UPLOAD_CATEGORIES_TYPE]: {
    title: 'Categories created',
    description: item =>
      item?.data?.success
        ? 'Refresh the page to see the changes.'
        : `Warning: ${
            BULK_UPLOAD_CATEGORIES_TYPE_ERRORS[item?.data?.error_codes?.[0]] ||
            ''
          }`,
    icon: createColoredIcon(FileSyncOutlined, TASK_BLACK),
    duration: 0 // 0 means the notification will stay until manually closed
  },
  [BENEFITS_BULK_CREATE]: {
    title: 'Tiers bulk creation',
    description: item =>
      item?.data?.success
        ? 'Tiers bulk creation completed'
        : BENEFITS_BULK_CREATE_ERRORS[item?.data?.error_codes?.[0]] || '',
    icon: createColoredIcon(UploadOutlined, TASK_BLACK)
  },
  [BENEFIT_CATEGORIES_BULK_CREATE]: {
    title: 'Categories bulk creation',
    description: item =>
      item?.data?.success
        ? 'Categories bulk creation completed'
        : BENEFIT_CATEGORIES_BULK_CREATE_ERRORS[item?.data?.error_codes?.[0]] ||
          '',
    icon: createColoredIcon(UploadOutlined, TASK_BLACK)
  },
  [BENEFIT_GROUPS_BULK_CREATE]: {
    title: 'Benefits bulk creation',
    description: item =>
      item?.data?.success
        ? 'Benefits bulk creation completed'
        : BENEFIT_GROUPS_BULK_CREATE_ERRORS[item?.data?.error_codes?.[0]] || '',
    icon: createColoredIcon(UploadOutlined, TASK_BLACK)
  },
  [INTERACTIONS_BULK_CREATE]: {
    title: 'Interactions bulk creation',
    description: item =>
      item?.data?.success
        ? 'Interactions bulk creation completed'
        : INTERACTIONS_BULK_CREATE[item?.data?.error_codes?.[0]] || '',
    icon: createColoredIcon(UploadOutlined, TASK_BLACK)
  },
  [INSURANCE_PLAN_BULK_UPLOAD]: {
    title: 'Plan bulk creation',
    description: item =>
      item?.data?.success
        ? 'Plan Upload Successful.'
        : `Plan Upload Unsuccessful. ${
            INSURANCE_PLAN_BULK_UPLOAD_ERRORS[item?.data?.error_codes?.[0]] ||
            ''
          }`,
    icon: createColoredIcon(UploadOutlined, TASK_BLACK)
  },
  [INSURANCE_PLAN_CLONED]: {
    title: 'Plan bulk copy',
    description: item =>
      item?.data?.success
        ? 'Plan Upload Successful.'
        : `Plan Upload Unsuccessful. ${
            INSURANCE_PLAN_BULK_UPLOAD_ERRORS[item?.data?.error_codes?.[0]] ||
            ''
          }`,
    icon: createColoredIcon(UploadOutlined, TASK_BLACK)
  },
  [USER_TAGGED]: {
    title: 'Mention',
    description: item =>
      `Mention from ${item?.data?.performer_name} on ${item?.source_type} ${item?.source_id}`,
    icon: createColoredIcon(MessageOutlined, MESSAGE_BLUE)
  },
  [TICKET_DELIVERABLES_BULK_CREATE]: {
    title: 'Deliverables bulk creation',
    description: item =>
      item?.data?.success
        ? 'Deliverables upload successful.'
        : `${
            TICKET_DELIVERABLES_BULK_CREATE_ERRORS[
              item?.data?.error_codes?.[0]
            ] || 'Deliverables upload unsuccessful.'
          }`,
    icon: createColoredIcon(UploadOutlined, TASK_BLACK)
  },
  [TICKET_BULK_ASSIGN_TO_PERFORMER]: {
    title: 'Bulk Assign',
    description: item => {
      const count = item?.data?.assigned_count || 0
      return item?.data?.success
        ? `${count} ${
            count === 1 ? 'ticket' : 'tickets'
          } assigned successfully.`
        : 'Failed to reassign tickets.'
    },
    icon: createColoredIcon(UploadOutlined, TASK_BLACK)
  },
  [TICKET_BULK_ASSIGN_TO_QUEUE]: {
    title: 'Bulk Assign',
    description: item => {
      const count = item?.data?.assigned_count || 0
      return item?.data?.success
        ? `${count} ${
            count === 1 ? 'ticket' : 'tickets'
          } assigned successfully.`
        : 'Failed to reassign tickets.'
    },
    icon: createColoredIcon(UploadOutlined, TASK_BLACK)
  },
  [TICKET_BULK_ASSIGN_TO_RECIPIENT]: {
    title: 'Bulk Assign',
    description: item => {
      const count = item?.data?.assigned_count || 0
      return item?.data?.success
        ? `Admin assigned you ${count} ${count === 1 ? 'ticket' : 'tickets'}.`
        : 'Failed to reassign tickets.'
    },
    icon: createColoredIcon(UploadOutlined, TASK_BLACK)
  },
  [TICKET_BULK_CHANGE_STATUS]: {
    title: 'Bulk Change Status',
    description: item =>
      item?.data?.success
        ? 'Status changed successful. Please refresh the page to see the changes.'
        : 'Status change unsuccessful.',
    icon: createColoredIcon(UploadOutlined, TASK_BLACK)
  },
  [BENEFIT_ORGS_BULK_UPLOAD]: {
    title: 'Bulk organizations upload',
    description: item =>
      item?.data?.success
        ? `Bulk upload of organizations completed for plan ${item?.source_id}.`
        : 'Bulk upload of organizations failed.',
    icon: createColoredIcon(UploadOutlined, TASK_BLACK)
  }
}

export const getFilteredTimedTypes = timed => {
  if (!timed) return []
  const now = dayjs()
  return timed.filter(i => dayjs(i.data.notify_at).isSameOrBefore(now))
}

export const isTimedType = item => [ALARM_TYPE, TASK_TYPE].includes(item?.type)

export const notificationOnClick = (item, shouldReturn) => {
  if (shouldReturn) return

  const { type, source_id, source_type, data } = item || {}

  const tagRoutes = {
    member_notes: `/members/${source_id}`,
    medical_notes: `/members/${source_id}?tab=medical`,
    ticket_notes: `/ticket/${source_id}`
  }

  if (type === BENEFIT_ORGS_BULK_UPLOAD) {
    window.open(`/benefits-cms/${source_id}`)
    return
  }

  if (type === INSURANCE_PLAN_BULK_UPLOAD) {
    window.open(`/benefits-cms`)
    return
  }

  if (type === USER_TAGGED && data?.tag_type) {
    const route = tagRoutes[data.tag_type] || `/ticket/${source_id}`
    window.open(route)
    return
  }

  if (source_type === 'Chat') {
    window.open(`/chat/${source_id}`)
    return
  }

  window.open(`/ticket/${source_id}`)
}

export const openNotification = (item, api) => {
  const notification = NOTIFICATIONS[item.type]

  if (!notification) return

  const description = notification.description(item)

  const shouldClickAbort = [
    BULK_CREATE_TICKETS,
    BULK_UPLOAD_CATEGORIES_TYPE,
    BENEFITS_BULK_CREATE,
    BENEFIT_CATEGORIES_BULK_CREATE,
    BENEFIT_GROUPS_BULK_CREATE,
    INSURANCE_PLAN_CLONED,
    INTERACTIONS_BULK_CREATE,
    TICKET_DELIVERABLES_BULK_CREATE,
    TICKET_BULK_ASSIGN_TO_PERFORMER,
    TICKET_BULK_ASSIGN_TO_QUEUE,
    TICKET_BULK_ASSIGN_TO_RECIPIENT,
    TICKET_BULK_CHANGE_STATUS
  ].includes(item.type)

  // Use notification-specific duration if defined, otherwise default to 4 seconds
  const duration =
    notification.duration !== undefined ? notification.duration : 4

  api.open({
    ...notification,
    description,
    onClick: () => notificationOnClick(item, shouldClickAbort),
    style: { cursor: 'pointer' },
    duration
  })
}
